import { FC, ReactNode, useEffect, useRef } from "react";

import { useDialog } from "@/hooks";
import {
  clearTelegramWidgetAuth,
  isTelegramMiniApp,
  readTelegramWidgetAuth,
  syncTelegramChrome,
  telegramStartParam,
  webApp,
} from "@/lib/telegram";
import routes from "@/routes";
import {
  getTelegramStatus,
  linkTelegram,
  linkTelegramWidget,
  loginWithTelegram,
  loginWithTelegramWidget,
} from "@/services";
import { dispatch, login, store, useSelector } from "@/store";
import {
  clearStoredAffiliateCode,
  clearStoredReferralCode,
  getStoredAffiliateCode,
  getStoredReferralCode,
} from "@/utils";
import { clearStoredAttribution, signupSourcePayload } from "@/utils/attribution";

/**
 * Makes the player site behave as a Telegram Mini App when it is opened as one,
 * and owns Telegram sign-in on the ordinary web too.
 *
 * Five jobs, none of which the rest of the app has to know about. The first
 * four only happen inside Telegram:
 *
 *  - tells Telegram the page is ready and asks for the full height, and keeps
 *    Telegram's chrome the same colour as the page;
 *  - publishes the usable viewport and the safe-area insets as CSS variables,
 *    because inside Telegram `100vh` is not the height the player can see;
 *  - wires Telegram's own back button to the router, so the hardware-ish back
 *    gesture moves through the app instead of closing it;
 *  - signs the player in from `initData` on launch, or attaches Telegram to the
 *    account they are already signed in to.
 *
 * And the fifth happens in a browser:
 *
 *  - finishes a **Login Widget** sign-in, which arrives as a redirect back to
 *    this page with Telegram's signed fields in the query string.
 *
 * Both sign-ins land in the same two shapes - a session, or a 2FA pending
 * token - and both prefer linking over opening a second account when a session
 * is already held, which is why they live together here.
 */

const MINI_APP_CLASS = "telegram-mini-app";

const applyViewport = (): void => {
  const app = webApp();
  if (!app) return;

  const root = document.documentElement;
  // The height Telegram actually gives the page. `100vh` inside the client
  // includes the area under its own header, so anything sized to the screen
  // reads this instead.
  root.style.setProperty("--tg-viewport-height", `${app.viewportHeight}px`);
  root.style.setProperty("--tg-viewport-stable-height", `${app.viewportStableHeight}px`);

  // Telegram's own bars sit over the page on fullscreen-capable clients; the
  // content inset is the part a layout must keep clear.
  const inset = app.contentSafeAreaInset ?? app.safeAreaInset;
  root.style.setProperty("--tg-safe-top", `${inset?.top ?? 0}px`);
  root.style.setProperty("--tg-safe-bottom", `${inset?.bottom ?? 0}px`);
};

/**
 * Telegram's back button, driven from the router.
 *
 * `routes` is used directly rather than `useNavigate` because this provider
 * sits outside the RouterProvider - it has to, so that the launch sign-in
 * happens before any route renders a logged-out screen.
 */
const wireBackButton = (): (() => void) => {
  const app = webApp();
  if (!app) return () => undefined;

  const onClick = () => routes.navigate(-1);
  app.BackButton.onClick(onClick);

  const sync = () => {
    const atRoot = routes.state.location.pathname === "/";
    if (atRoot) app.BackButton.hide();
    else app.BackButton.show();
  };

  sync();
  const unsubscribe = routes.subscribe(sync);

  return () => {
    unsubscribe();
    app.BackButton.offClick(onClick);
    app.BackButton.hide();
  };
};

export const TelegramProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { openDialog } = useDialog();
  const isLoggedIn = useSelector((store) => store.auth.isLoggedIn);

  // One attempt per page load. Telegram re-renders this tree on theme and
  // viewport events, and a second sign-in call would be a second account
  // attempt for a player whose first one is still in flight.
  const attempted = useRef(false);

  useEffect(() => {
    const app = webApp();
    if (!app || !isTelegramMiniApp()) return;

    document.documentElement.classList.add(MINI_APP_CLASS);

    app.ready();
    app.expand();
    // Otherwise a downward drag on a game board closes the Mini App instead of
    // being handled by the board.
    app.disableVerticalSwipes?.();

    applyViewport();
    syncTelegramChrome();

    const onViewport = () => applyViewport();
    const onTheme = () => syncTelegramChrome();
    app.onEvent("viewportChanged", onViewport);
    app.onEvent("themeChanged", onTheme);

    const unwireBack = wireBackButton();

    return () => {
      app.offEvent("viewportChanged", onViewport);
      app.offEvent("themeChanged", onTheme);
      unwireBack();
      document.documentElement.classList.remove(MINI_APP_CLASS);
    };
  }, []);

  useEffect(() => {
    if (!isTelegramMiniApp() || attempted.current) return;
    const initData = webApp()?.initData;
    if (!initData) return;

    attempted.current = true;

    const run = async () => {
      if (isLoggedIn) {
        // Already signed in - from an email login, or from a session this
        // browser kept. Attaching Telegram here is what stops the same person
        // ending up with two accounts. Quiet on failure: it is nobody's
        // request, and "already linked to another player" is not something to
        // interrupt a launch with.
        try {
          const status = await getTelegramStatus();
          if (status.available && !status.linked) {
            await linkTelegram({ initData });
          }
          // Telegram keeps the webview's storage between launches and between
          // its own accounts, so the session found here may belong to the
          // player of another Telegram account on this device. The launch
          // decides who is playing: sign in again as its owner. The id is only
          // compared here, the backend proves it from the signed payload.
          const launchId = webApp()?.initDataUnsafe?.user?.id;
          const otherAccount =
            status.linked && launchId !== undefined && status.telegramId !== String(launchId);
          if (!otherAccount) return;
        } catch (error) {
          // A kept session that has expired answers 401, which signs the store
          // out. The launch still has to end signed in, so it goes on to the
          // sign-in below; any other failure leaves the session as it is.
          if (store.getState().auth.isLoggedIn) {
            console.warn("Telegram link skipped:", error);
            return;
          }
        }
      }

      try {
        // A code from the launch link wins, then one captured from a web visit
        // in this browser - the Mini App may well be opened after the player
        // followed a referral link on the site. The backend also reads the
        // launch link's code out of the signed payload itself, so this is
        // belt and braces for the stored case.
        const startParam = telegramStartParam()?.replace(/^ref_/i, "").trim();
        const affiliate = getStoredAffiliateCode();

        const result = await loginWithTelegram({
          initData,
          referralCode: startParam || getStoredReferralCode(),
          affCode: affiliate?.affCode,
          clickId: affiliate?.clickId,
          affSubIds: affiliate?.subIds,
          ...signupSourcePayload(),
        });

        if (result?.requires2fa) {
          openDialog("2fa", { pendingToken: result.pendingToken });
          return;
        }

        dispatch(login(result));

        // Spent: the account has been opened with them, and leaving them behind
        // would attribute a later signup on this device to the same referrer.
        clearStoredReferralCode();
        clearStoredAffiliateCode();
        clearStoredAttribution();
      } catch (error) {
        // Leave the player on the normal logged-out site with its own sign-in
        // button rather than a dead end: a refused launch is recoverable by
        // signing in the ordinary way.
        console.warn("Telegram sign-in failed:", error);
      }
    };

    void run();
  }, [isLoggedIn, openDialog]);

  // One attempt per page load, for the same reason as the launch sign-in above.
  const widgetAttempted = useRef(false);

  useEffect(() => {
    if (widgetAttempted.current) return;

    const authQuery = readTelegramWidgetAuth();
    if (!authQuery) return;

    widgetAttempted.current = true;

    // Off the address bar before the request goes out, not after it comes back.
    // Until this runs the signed payload is in the URL, which means it is in
    // the browser's history and in the `Referer` of anything the page loads
    // next; the value we need is already in hand. The backend spends each
    // payload on first use, so there is nothing to retry with anyway.
    clearTelegramWidgetAuth();

    const run = async () => {
      if (isLoggedIn) {
        // Same reasoning as a launch made while signed in: attach Telegram to
        // the account that already exists rather than opening a second one, and
        // stay quiet if it belongs to somebody else.
        try {
          const status = await getTelegramStatus();
          if (status.available && !status.linked) {
            await linkTelegramWidget({ authQuery });
          }
          return;
        } catch (error) {
          // An expired session is signed out by its 401 before the payload was
          // spent, so the sign-in below can still use it.
          if (store.getState().auth.isLoggedIn) {
            console.warn("Telegram link skipped:", error);
            return;
          }
        }
      }

      try {
        // No `startapp=` on this path - a widget sign-in carries no payload of
        // its own - so a referral can only come from what this browser stored
        // on the way in. A code left with the bot by `/start ref_…` is still
        // waiting in Redis and is picked up server-side either way.
        const affiliate = getStoredAffiliateCode();

        const result = await loginWithTelegramWidget({
          authQuery,
          referralCode: getStoredReferralCode(),
          affCode: affiliate?.affCode,
          clickId: affiliate?.clickId,
          affSubIds: affiliate?.subIds,
          ...signupSourcePayload(),
        });

        if (result?.requires2fa) {
          openDialog("2fa", { pendingToken: result.pendingToken });
          return;
        }

        dispatch(login(result));

        clearStoredReferralCode();
        clearStoredAffiliateCode();
        clearStoredAttribution();
      } catch (error) {
        // The player is left on the ordinary logged-out site with the widget
        // still on the sign-in form, which is a working retry.
        console.warn("Telegram sign-in failed:", error);
      }
    };

    void run();
  }, [isLoggedIn, openDialog]);

  return <>{children}</>;
};
